import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import { Radio, Play, Loader } from 'lucide-react'
import api from '../api'

const EXAMPLES = [
  { url:'wss://ws.postman-echo.com/raw', desc:'Postman WebSocket Echo – phản hồi lại mọi tin nhắn' },
]

export default function WsTool() {
  const [url,       setUrl]       = useState('wss://echo.websocket.org')
  const [message,   setMessage]   = useState('{"type":"ping","data":"hello"}')
  const [timeoutMs, setTimeoutMs] = useState(10000)
  const [result,    setResult]    = useState(null)
  const [loading,   setLoading]   = useState(false)

  const test = async () => {
    if (!url.trim()) return toast.error('Vui lòng nhập URL WebSocket')
    setLoading(true); setResult(null)
    try {
      const res = await api.post('/test-websocket', {
        url:     url.trim(),
        message: message.trim() || undefined,
        timeout: timeoutMs,
      })
      setResult(res.data)
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Lỗi không xác định'
      toast.error('Lỗi: ' + msg)
      setResult({ status:'error', error: msg })
    } finally { setLoading(false) }
  }

  const info = result ? (
    result.status === 'success' || result.status === 'connected'
      ? { color:'#34d399', icon:'✅', label: result.status==='connected' ? 'Kết nối thành công' : 'Nhận phản hồi thành công' }
      : result.status === 'timeout'
        ? { color:'#fbbf24', icon:'⏱', label:'Timeout – không nhận được phản hồi' }
        : { color:'#f87171', icon:'❌', label:'Lỗi kết nối' }
  ) : null

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Title */}
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ width:40, height:40, borderRadius:10, flexShrink:0,
          background:'linear-gradient(135deg,var(--accent3),#0d9488)',
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:'0 0 18px rgba(52,211,153,.35)' }}>
          <Radio size={20} color="#000" />
        </div>
        <div>
          <h1 style={{ fontSize:18, fontWeight:700 }}>WebSocket Tester</h1>
          <p style={{ fontSize:13, color:'var(--text3)' }}>Kiểm tra kết nối và giao tiếp WebSocket endpoint</p>
        </div>
      </div>

      {/* Form */}
      <div className="grad-border p-5 space-y-4">
        <div>
          <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>WebSocket URL</label>
          <input className="inp font-mono" style={{ fontSize:13 }}
            placeholder="wss://your-server.com/ws"
            value={url} onChange={e => setUrl(e.target.value)}
            onKeyDown={e => e.key==='Enter' && test()} />
          <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>
            Dùng <code style={{ color:'var(--accent)', fontFamily:'JetBrains Mono,monospace' }}>wss://</code> cho HTTPS hoặc{' '}
            <code style={{ color:'var(--accent)', fontFamily:'JetBrains Mono,monospace' }}>ws://</code> cho HTTP
          </div>
        </div>

        <div>
          <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>
            Tin nhắn gửi <span style={{ fontWeight:400 }}>(để trống = chỉ test kết nối)</span>
          </label>
          <textarea className="inp font-mono" rows={4} style={{ fontSize:13 }}
            placeholder={'{"type":"ping"}'}
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>

        <div style={{ display:'flex', alignItems:'flex-end', gap:16 }}>
          <div>
            <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>Timeout</label>
            <select className="inp" style={{ width:140 }} value={timeoutMs}
              onChange={e => setTimeoutMs(parseInt(e.target.value))}>
              <option value={5000}>5 giây</option>
              <option value={10000}>10 giây</option>
              <option value={30000}>30 giây</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={test} disabled={loading}>
            {loading
              ? <><Loader size={14} className="animate-spin" /> Đang kết nối...</>
              : <><Play size={14} /> Kết nối & Test</>}
          </button>
        </div>

        {/* Result */}
        {result && info && (
          <div className="fade-in" style={{ borderTop:'1px solid var(--border)', paddingTop:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
              <span style={{ fontSize:15, fontWeight:700, color: info.color }}>
                {info.icon} {info.label}
              </span>
              {(result.latency || result.connectTime) && (
                <span className="chip chip-blue" style={{ fontFamily:'JetBrains Mono,monospace' }}>
                  {result.latency || result.connectTime}ms
                </span>
              )}
            </div>

            {result.response && (
              <div>
                <div style={{ fontSize:12, color:'var(--text3)', marginBottom:6 }}>📥 Phản hồi nhận được:</div>
                <pre className="log-output" style={{ fontSize:13 }}>{result.response}</pre>
              </div>
            )}
            {result.message && (
              <div style={{ fontSize:13, color:'var(--text2)', padding:'10px 14px',
                borderRadius:8, background:'var(--bg)', border:'1px solid var(--border)' }}>
                {result.message}
              </div>
            )}
            {result.error && (
              <div className="log-output log-error" style={{ fontSize:13 }}>{result.error}</div>
            )}
          </div>
        )}
      </div>

      {/* Examples */}
      <div className="grad-border p-5">
        <h3 style={{ fontSize:15, fontWeight:700, marginBottom:12 }}>🔗 URL WebSocket ví dụ</h3>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {EXAMPLES.map(e => (
            <button key={e.url} onClick={() => setUrl(e.url)}
              style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'10px 14px',
                borderRadius:8, background:'var(--bg)', border:'1px solid var(--border)',
                cursor:'pointer', textAlign:'left', transition:'all .18s', width:'100%' }}
              onMouseEnter={ev => { ev.currentTarget.style.borderColor = 'var(--accent3)'; ev.currentTarget.style.background = 'rgba(52,211,153,.04)'; }}
              onMouseLeave={ev => { ev.currentTarget.style.borderColor = 'var(--border)'; ev.currentTarget.style.background = 'var(--bg)'; }}>
              <span className="chip chip-green" style={{ flexShrink:0, fontFamily:'JetBrains Mono,monospace', fontSize:11 }}>WS</span>
              <div style={{ minWidth:0 }}>
                <div style={{ fontFamily:'JetBrains Mono,monospace', fontSize:13, color:'var(--accent)' }}>{e.url}</div>
                <div style={{ fontSize:12, color:'var(--text3)', marginTop:2 }}>{e.desc}</div>
              </div>
            </button>
          ))}
        </div>

        <div style={{ marginTop:16, padding:'12px 14px', borderRadius:8,
          background:'var(--bg)', border:'1px solid var(--border)' }}>
          <div style={{ fontSize:12, color:'var(--text3)', marginBottom:8 }}>📖 Hướng dẫn:</div>
          <ol style={{ fontSize:13, color:'var(--text2)', display:'flex', flexDirection:'column', gap:4, paddingLeft:20 }}>
            <li>Nhập URL WebSocket (bắt đầu bằng ws:// hoặc wss://)</li>
            <li>Nhập tin nhắn JSON/text muốn gửi (bỏ trống = chỉ test kết nối)</li>
            <li>Nhấn "Kết nối & Test" để kiểm tra</li>
            <li>Dùng echo server để test cơ bản trước khi test server thật</li>
          </ol>
        </div>
      </div>
    </div>
  )
}
